import { captureException } from "@sentry/react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestPasswordReset } from "@/lib/api";
import { SENTRY_ENABLED } from "@/lib/sentry";

/**
 * Firebase codes for "the credentials were wrong", an expected outcome of a
 * login form rather than an application fault, so they are kept out of Sentry.
 */
const EXPECTED_AUTH_ERRORS = [
	"auth/invalid-credential",
	"auth/wrong-password",
	"auth/user-not-found",
	"auth/too-many-requests",
];

type LoginCardProps = {
	title: string;
	description: string;
	/** Resolves on success, rejects with the Firebase error on failure. */
	onSubmit: (email: string, password: string) => Promise<void>;
	/**
	 * Distinguishes which session's login failed in Sentry. `auth_login` is the
	 * `/app` session, `ops_auth_login` the operations one.
	 */
	sentryContext: string;
	/** The email to start from, when it is already known, as after a password is set. */
	initialEmail?: string;
};

/**
 * The shared email and password login form, used by the sign-in addresses and by
 * the desktop's in-place logins at `/operations` and `/organization`. Purely
 * presentational. It owns the field state and the forgot-password dialog, and
 * knows nothing about where the caller redirects afterwards.
 */
export function LoginCard({
	title,
	description,
	onSubmit,
	sentryContext,
	initialEmail,
}: LoginCardProps) {
	const [email, setEmail] = useState(initialEmail ?? "");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [resetLoading, setResetLoading] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);

		try {
			await onSubmit(email, password);
			toast.success("Logged in successfully!");
		} catch (error) {
			const isExpectedAuthError =
				error instanceof Error &&
				"code" in error &&
				EXPECTED_AUTH_ERRORS.includes((error as { code: string }).code);

			if (SENTRY_ENABLED && !isExpectedAuthError) {
				captureException(error, { tags: { context: sentryContext } });
			}

			toast.error("Email or password is incorrect");
		} finally {
			setLoading(false);
		}
	};

	const handleForgotPassword = async (e: React.FormEvent) => {
		e.preventDefault();
		setResetLoading(true);

		try {
			// The page's own address, without query or fragment: it names the
			// identity home the account is looked for in.
			await requestPasswordReset(
				resetEmail,
				`${window.location.origin}${window.location.pathname}`
			);
		} catch (error) {
			if (SENTRY_ENABLED) {
				captureException(error, { tags: { context: "auth_forgot_password" } });
			}
		} finally {
			// Always the same toast, whether or not the account exists, or the form
			// doubles as an email-enumeration oracle.
			toast.success(
				"If this email has an account here, a link to set a new password is on its way. It can take a few minutes to arrive."
			);
			setShowForgotPassword(false);
			setResetEmail("");
			setResetLoading(false);
		}
	};

	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle>{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</CardHeader>
				<CardContent>
					<form className="space-y-4" onSubmit={handleSubmit}>
						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								onChange={(e) => setEmail(e.target.value)}
								placeholder="your@email.com"
								required
								type="email"
								value={email}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Input
									className="pr-10"
									id="password"
									onChange={(e) => setPassword(e.target.value)}
									placeholder="••••••••"
									required
									type={showPassword ? "text" : "password"}
									value={password}
								/>
								<Button
									aria-label={showPassword ? "Hide password" : "Show password"}
									className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
									onClick={() => setShowPassword(!showPassword)}
									size="sm"
									type="button"
									variant="ghost"
								>
									{showPassword ? (
										<EyeOffIcon className="h-4 w-4 text-muted-foreground" />
									) : (
										<EyeIcon className="h-4 w-4 text-muted-foreground" />
									)}
								</Button>
							</div>
						</div>
						<Button className="w-full" disabled={loading} type="submit">
							{loading ? "Please wait..." : "Login"}
						</Button>
						<Button
							className="w-full"
							onClick={() => {
								setShowForgotPassword(true);
								setResetEmail(email);
							}}
							type="button"
							variant="link"
						>
							Forgot password?
						</Button>
					</form>
				</CardContent>
			</Card>

			<Dialog onOpenChange={setShowForgotPassword} open={showForgotPassword}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Reset your password</DialogTitle>
						<DialogDescription>
							Enter your email and we'll send you a link to set a new password.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={handleForgotPassword}>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="reset-email">Email</Label>
								<Input
									id="reset-email"
									onChange={(e) => setResetEmail(e.target.value)}
									placeholder="your@email.com"
									required
									type="email"
									value={resetEmail}
								/>
							</div>
						</div>
						<DialogFooter>
							<Button
								onClick={() => setShowForgotPassword(false)}
								type="button"
								variant="outline"
							>
								Cancel
							</Button>
							<Button disabled={resetLoading} type="submit">
								{resetLoading ? "Sending..." : "Send link"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</>
	);
}
